import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { useParams } from "react-router-dom";
import { ordersApi } from "../../api/orders";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const PAGE_SIZE = 20;

const STATUS_META = {
  received: { label: "Recibido", color: "info" },
  awaiting_payment: { label: "Esperando pago", color: "warning" },
  payment_pending: { label: "Pago pendiente", color: "warning" },
  paid: { label: "Pagado", color: "success" },
  payment_failed: { label: "Pago rechazado", color: "error" },
  requires_review: { label: "Revisar", color: "error" },
  processing: { label: "Preparando", color: "info" },
  ready: { label: "Listo", color: "success" },
  shipped: { label: "Enviado", color: "info" },
  delivered: { label: "Entregado", color: "success" },
  cancelled: { label: "Cancelado", color: "default" },
  refunded: { label: "Reembolsado", color: "default" },
};

const PAYMENT_META = {
  not_started: { label: "Sin iniciar", color: "default" },
  pending: { label: "Pendiente", color: "warning" },
  in_process: { label: "Procesando", color: "warning" },
  authorized: { label: "Autorizado", color: "info" },
  approved: { label: "Aprobado", color: "success" },
  rejected: { label: "Rechazado", color: "error" },
  cancelled: { label: "Cancelado", color: "default" },
  refunded: { label: "Reembolsado", color: "default" },
  charged_back: { label: "Contracargo", color: "error" },
  unknown: { label: "Desconocido", color: "default" },
};

function formatMoney(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currency || "ARS",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function statusChip(status) {
  const meta = STATUS_META[status] || { label: status || "Sin estado", color: "default" };
  return <Chip size="small" label={meta.label} color={meta.color} sx={{ fontWeight: 850 }} />;
}

function paymentChip(status) {
  const meta = PAYMENT_META[status] || { label: status || "Sin estado", color: "default" };
  return <Chip size="small" label={meta.label} color={meta.color} variant="outlined" sx={{ fontWeight: 850 }} />;
}

function isCoordinatedOrder(order) {
  if (!order) return false;
  if (order.paymentMode) return order.paymentMode === "coordinate";
  return order.payment?.provider === "coordinate";
}

function orderPaymentChip(order) {
  if (isCoordinatedOrder(order)) {
    return (
      <Chip
        size="small"
        label="Pago a coordinar"
        color="info"
        variant="outlined"
        sx={{ fontWeight: 850 }}
      />
    );
  }
  return paymentChip(order?.paymentStatus);
}

function possibleStatuses(order) {
  if (!order || ["cancelled", "refunded", "delivered"].includes(order.status)) {
    return [];
  }

  if (isCoordinatedOrder(order) || order.paymentStatus === "approved") {
    return [
      { value: "processing", label: "Preparando" },
      { value: "ready", label: "Listo para retirar" },
      { value: "shipped", label: "Enviado" },
      { value: "delivered", label: "Entregado" },
      { value: "cancelled", label: "Cancelar pedido" },
    ];
  }

  return [{ value: "cancelled", label: "Cancelar pedido" }];
}

export default function ProjectOrdersPage() {
  const { projectId } = useParams();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [orders, setOrders] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [query, setQuery] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [nextStatus, setNextStatus] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId) || activeProject,
    [projects, projectId, activeProject]
  );
  const ordersEnabled = isProjectModuleEnabled(project || contextProject, "orders");
  const ecommerceEnabled = isProjectModuleEnabled(project || contextProject, "ecommerce");

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let active = true;
    projectsApi.get(projectId)
      .then((data) => {
        if (!active) return;
        setProject(data.project || null);
        if (data.project) setActiveProject(data.project);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [projectId, setActiveProject]);

  const loadOrders = React.useCallback(async () => {
    if (!ordersEnabled) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const data = await ordersApi.list(projectId, {
        page,
        pageSize: PAGE_SIZE,
        q: query.trim(),
        status,
      });
      setOrders(data.orders || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setOrders([]);
      setTotal(0);
      setError(err.message || "No se pudieron cargar los pedidos");
    } finally {
      setLoading(false);
    }
  }, [ordersEnabled, projectId, page, query, status]);

  React.useEffect(() => {
    const timer = window.setTimeout(loadOrders, 250);
    return () => window.clearTimeout(timer);
  }, [loadOrders]);

  const openOrder = async (order) => {
    try {
      setSelectedOrder(order);
      setNextStatus("");
      setDetailLoading(true);
      const data = await ordersApi.get(projectId, order.id || order.orderId || order._id);
      setSelectedOrder(data.order || order);
    } catch (err) {
      setError(err.message || "No se pudo abrir el pedido");
    } finally {
      setDetailLoading(false);
    }
  };

  const updateStatus = async () => {
    if (!selectedOrder || !nextStatus) return;
    try {
      setUpdating(true);
      setError("");
      const orderId = selectedOrder.id || selectedOrder.orderId || selectedOrder._id;
      const data = await ordersApi.updateStatus(projectId, orderId, nextStatus);
      setSelectedOrder(data.order || selectedOrder);
      setNextStatus("");
      await loadOrders();
    } catch (err) {
      setError(err.message || "No se pudo actualizar el pedido");
    } finally {
      setUpdating(false);
    }
  };

  if (!ordersEnabled && !loading) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Pedidos"
        description="Este nivel permite registrar compradores, coordinar retiro o entrega y seguir el estado de cada pedido sin exigir Mercado Pago."
      />
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paidCount = orders.filter((order) => order.paymentStatus === "approved").length;
  const pendingCount = orders.filter((order) => ["received", "awaiting_payment", "payment_pending", "requires_review"].includes(order.status)).length;

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
            Pedidos
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
            {project?.name || contextProject?.name || projectId} · retiro, entrega y seguimiento
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshRoundedIcon />}
          onClick={loadOrders}
          variant="outlined"
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
        >
          Actualizar
        </Button>
      </Stack>

      {!ecommerceEnabled && (
        <Alert severity="success">
          <strong>Pedidos</strong> funciona sin Mercado Pago: los pedidos ingresan para coordinar pago, retiro o entrega con el comprador.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)", fontWeight: 800 }}>TOTAL</Typography>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark, mt: 0.5 }}>{total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)", fontWeight: 800 }}>PAGADOS EN ESTA PÁGINA</Typography>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark, mt: 0.5 }}>{paidCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)", fontWeight: 800 }}>PENDIENTES EN ESTA PÁGINA</Typography>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark, mt: 0.5 }}>{pendingCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2.5 }}>
            <TextField
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              label="Buscar pedido, cliente o email"
              fullWidth
              InputProps={{ startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: "rgba(25,36,47,0.45)" }} /> }}
            />
            <TextField
              select
              label="Estado"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
              sx={{ minWidth: { xs: "100%", md: 220 } }}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(STATUS_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>{meta.label}</MenuItem>
              ))}
            </TextField>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 7 }} spacing={2}>
              <CircularProgress />
              <Typography>Cargando pedidos...</Typography>
            </Stack>
          ) : orders.length === 0 ? (
            <Alert severity="info">Todavía no hay pedidos con estos filtros.</Alert>
          ) : (
            <>
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid rgba(25,36,47,0.07)", borderRadius: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#F6F7FB" }}>
                      <TableCell sx={{ fontWeight: 900 }}>Pedido</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Entrega</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Pago</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow
                        key={order.id || order.orderId || order._id}
                        hover
                        onClick={() => openOrder(order)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{order.orderNumber || "Pedido"}</Typography>
                          <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.56)" }}>{formatDate(order.createdAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 800 }}>{order.customer?.fullName || "—"}</Typography>
                          <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.56)" }}>{order.customer?.email || ""}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            {order.fulfillment?.method === "delivery" ? <LocalShippingRoundedIcon fontSize="small" /> : <StorefrontRoundedIcon fontSize="small" />}
                            <Typography variant="body2">{order.fulfillment?.method === "delivery" ? "Envío" : "Retiro"}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{statusChip(order.status)}</TableCell>
                        <TableCell>{orderPaymentChip(order)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900 }}>{formatMoney(order.total, order.currency)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Stack alignItems="center" sx={{ mt: 2.5 }}>
                  <Pagination page={page} count={totalPages} onChange={(_event, value) => setPage(value)} />
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(selectedOrder)} onClose={() => setSelectedOrder(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>
          {selectedOrder?.orderNumber || "Detalle del pedido"}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress /></Stack>
          ) : selectedOrder ? (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="overline" sx={{ fontWeight: 900 }}>CLIENTE</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{selectedOrder.customer?.fullName}</Typography>
                  <Typography>{selectedOrder.customer?.email}</Typography>
                  <Typography>{selectedOrder.customer?.numberphone}</Typography>
                  {selectedOrder.customer?.dni && <Typography>DNI: {selectedOrder.customer.dni}</Typography>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="overline" sx={{ fontWeight: 900 }}>ENTREGA</Typography>
                  <Typography sx={{ fontWeight: 900 }}>
                    {selectedOrder.fulfillment?.method === "delivery" ? "Envío a domicilio" : "Retiro en el local"}
                  </Typography>
                  {selectedOrder.fulfillment?.address && (
                    <Typography>
                      {selectedOrder.fulfillment.address.street} {selectedOrder.fulfillment.address.streetNumber}, {selectedOrder.fulfillment.address.city}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    {statusChip(selectedOrder.status)}
                    {orderPaymentChip(selectedOrder)}
                  </Stack>
                </Grid>
              </Grid>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950, mb: 1.5 }}>Productos</Typography>
                <Stack spacing={1.25}>
                  {(selectedOrder.items || []).map((item, index) => (
                    <Stack key={`${item.productId || index}-${index}`} direction="row" justifyContent="space-between" spacing={2} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "#F6F7FB" }}>
                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>{item.name}</Typography>
                        {item.variantLabel ? (
                          <Typography variant="body2" sx={{ color: BRAND.orange, fontWeight: 850 }}>
                            {item.variantLabel}{item.variantSku ? ` · SKU ${item.variantSku}` : ""}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>
                          {item.quantity} × {formatMoney(item.unitPrice, selectedOrder.currency)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 900 }}>{formatMoney(item.lineTotal, selectedOrder.currency)}</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.orange }}>{formatMoney(selectedOrder.total, selectedOrder.currency)}</Typography>
                </Stack>
              </Box>

              {selectedOrder.notes && <Alert severity="info">{selectedOrder.notes}</Alert>}

              {possibleStatuses(selectedOrder).length > 0 && (
                <Card elevation={0} sx={{ bgcolor: "#F6F7FB", borderRadius: 3 }}>
                  <CardContent>
                    <Typography sx={{ fontWeight: 950, mb: 1.5 }}>Actualizar estado</Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                      <TextField select value={nextStatus} onChange={(event) => setNextStatus(event.target.value)} label="Nuevo estado" fullWidth>
                        {possibleStatuses(selectedOrder).map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                      <Button
                        variant="contained"
                        disabled={!nextStatus || updating}
                        onClick={updateStatus}
                        sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, minWidth: 150 }}
                      >
                        {updating ? "Guardando..." : "Actualizar"}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedOrder(null)} sx={{ textTransform: "none", fontWeight: 900 }}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
